import { Stack, TextField } from '@mui/material';
import Grid2 from '@mui/material/Unstable_Grid2';
import React, { forwardRef, useImperativeHandle } from 'react';
import type { useForm } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import { _ } from '/@/locale';
import { changePassword } from '/@/store/session/actions';
import type { ProfileV2FormData } from '../..';
import TabContainer from '../../components/tab-container';

export interface PasswordFormData {
    currentPassword: string;
    newPassword: string;
    confirmPassword: string;
}

const PasswordEditTab = forwardRef(({ form }: { form: ReturnType<typeof useForm<ProfileV2FormData>> }, ref) => {
    const dispatch = useDispatch();

    const onSubmit = React.useCallback(
        async (data: PasswordFormData) => {
            // Submit password change
            try {
                await dispatch(
                    changePassword({
                        currentPassword: data.currentPassword,
                        password: data.newPassword,
                    })
                );
                form.resetField('currentPassword');
                form.resetField('newPassword');
                form.resetField('confirmPassword');
            } catch {
                form.setError('currentPassword', { message: _('La contraseña no es correcta') });
                throw new Error(_('La contraseña no es correcta'));
            }
        },
        [dispatch, form]
    );

    useImperativeHandle(
        ref,
        () => ({
            onSubmit,
        }),
        [onSubmit]
    );

    const errors = form.formState.errors;

    return (
        <TabContainer
            title={_('Cambiar contraseña')}
            description={_('Para actualizar su contraseña, confirme su contraseña actual.')}
        >
            <Grid2 container columns={9}>
                <Grid2 xs={12} md={3}>
                    <Stack component='form' noValidate onSubmit={(e) => e.preventDefault()} spacing={3} py={2}>
                        <TextField
                            type='password'
                            autoComplete='current-password'
                            label={_('Contraseña actual')}
                            {...form.register('currentPassword', {
                                required: _('La contraseña actual es requerida'),
                                deps: ['newPassword', 'confirmPassword'],
                            })}
                            error={!!errors.currentPassword}
                            helperText={errors.currentPassword?.message}
                            fullWidth
                        />
                        <TextField
                            type='password'
                            autoComplete='new-password'
                            label={_('Nueva contraseña')}
                            {...form.register('newPassword', {
                                required: _('La nueva contraseña es requerida'),
                                minLength: {
                                    value: 8,
                                    message: _('La contraseña debe tener al menos 8 caracteres'),
                                },
                                deps: ['confirmPassword'],
                                validate: {
                                    differentFromCurrent: (value) => {
                                        const currentPassword = form.watch('currentPassword');
                                        if (value === currentPassword) {
                                            return _('La nueva contraseña no puede ser igual a la contraseña actual');
                                        }
                                        return true;
                                    },
                                },
                            })}
                            error={!!errors.newPassword}
                            helperText={errors.newPassword?.message}
                            fullWidth
                        />
                        <TextField
                            type='password'
                            autoComplete='new-password'
                            label={_('Confirmar nueva contraseña')}
                            {...form.register('confirmPassword', {
                                required: _('Debes confirmar la nueva contraseña'),
                                validate: {
                                    passwordMatch: (value) => {
                                        const newPassword = form.watch('newPassword');
                                        if (value !== newPassword) {
                                            return _('Las contraseñas no coinciden');
                                        }
                                        return true;
                                    },
                                },
                            })}
                            error={!!errors.confirmPassword}
                            helperText={errors.confirmPassword?.message}
                            fullWidth
                        />
                    </Stack>
                </Grid2>
            </Grid2>
        </TabContainer>
    );
});

export default PasswordEditTab;
